import { useState } from 'react';
import { cx } from './cx';

export interface NumberInputProps {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  /** Digits after the point. 0 (default) allows whole numbers only. */
  decimals?: number | undefined;
  min?: number | undefined;
  max?: number | undefined;
  /** Shown after the box, for example "kg". */
  unit?: string | undefined;
  id?: string | undefined;
  'aria-label'?: string | undefined;
  'aria-describedby'?: string | undefined;
  invalid?: boolean | undefined;
  disabled?: boolean | undefined;
  size?: 'md' | 'lg' | undefined;
  placeholder?: string | undefined;
  className?: string | undefined;
}

function show(value: number | undefined): string {
  return value === undefined ? '' : String(value);
}

function parse(text: string, decimals: number): number | undefined {
  const t = text.trim().replace(/,/g, '.');
  if (t === '') return undefined;
  const pattern = decimals > 0 ? new RegExp(`^\\d*\\.?\\d{0,${decimals}}$`) : /^\d+$/;
  if (!pattern.test(t) || t === '.') return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * A number box for the counter: opens the numeric keypad on phones, selects what is in it when
 * you tab in (so typing replaces it) and never shows "NaN". Empty means "not entered".
 */
export function NumberInput({
  value,
  onChange,
  decimals = 0,
  min,
  max,
  unit,
  id,
  invalid,
  disabled,
  size = 'md',
  placeholder,
  className,
  ...aria
}: NumberInputProps) {
  const [text, setText] = useState(show(value));
  const [seen, setSeen] = useState(value);
  if (seen !== value) {
    setSeen(value);
    if (parse(text, decimals) !== value) setText(show(value));
  }

  const change = (next: string): void => {
    setText(next);
    const n = parse(next, decimals);
    if (n === undefined) {
      onChange(undefined);
      return;
    }
    if ((min !== undefined && n < min) || (max !== undefined && n > max)) {
      onChange(undefined);
      return;
    }
    onChange(n);
  };

  const outOfRange = (() => {
    const n = parse(text, decimals);
    return n !== undefined && ((min !== undefined && n < min) || (max !== undefined && n > max));
  })();

  return (
    <span className={cx('eb-money', className)}>
      <span className="eb-money__row">
        <input
          id={id}
          className="eb-input eb-money__input"
          data-size={size}
          inputMode={decimals > 0 ? 'decimal' : 'numeric'}
          autoComplete="off"
          value={text}
          disabled={disabled}
          placeholder={placeholder}
          aria-invalid={invalid || outOfRange ? true : undefined}
          aria-label={aria['aria-label']}
          aria-describedby={aria['aria-describedby']}
          onFocus={(e) => e.currentTarget.select()}
          onChange={(e) => change(e.target.value)}
        />
        {unit ? (
          <span className="eb-money__cur" aria-hidden="true">
            {unit}
          </span>
        ) : null}
      </span>
    </span>
  );
}
